import React, { useEffect, useState } from 'react'
import { decamelize } from 'humps'

import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import {
  Button,
  BUTTON_THEME,
  CheckBox,
  IconButton,
  Input,
  Select,
} from '@st-operations/uikit/src/v2/index.js'

import { useDestinations, useRegions } from '_hooks'
import convertObjAttrsSnakeToCamelCase from '_lib/convertObjAttrsSnakeToCamelCase'
import SelectedDestinations from '../../../../js/pages/audience-creator/components/selected-destinations'
import NameInput, { transformName } from '../name-input'

import './styles.scss'

const ModalBulkEditAudience = ({
  audiences,
  isOpen,
  actionLabel = 'Bulk Edit',
  onSubmit = () => {},
  onClose = () => {},
  schema = {},
}) => {
  const [audiencesMap, setAudiencesMap] = useState({})
  const [isApplyAll, setIsApplyAll] = useState(false)

  const regions = useRegions()
  const destinations = useDestinations()

  const handleChange = (id, key, val) => {
    if (isApplyAll) {
      setAudiencesMap((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            { ...audience, [key]: val },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiencesMap((prev) => ({
        ...prev,
        [id]: { ...prev[id], [key]: val },
      }))
    }
  }

  const handleDestinationsConfigChange = (audience) => (value, key) => {
    handleChange(audience._id, decamelize(key), value?.value ?? value)
  }

  const handleNameInput = (id) => (nameIndex) => (event) => {
    const { value } = event.target

    if (isApplyAll) {
      setAudiencesMap((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            {
              ...audience,
              name: audience.name.map((part, index) => (index === nameIndex ? value : part)),
            },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiencesMap((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: prev[id].name.map((part, index) => (index === nameIndex ? value : part)),
        },
      }))
    }
  }

  const handleAddNameInput = (id) => (index) => () => {
    if (isApplyAll) {
      setAudiencesMap((prev) => {
        const newAudiences = Object.fromEntries(
          Object.entries(prev).map(([audienceId, audience]) => [
            audienceId,
            {
              ...audience,
              name: [...audience.name.slice(0, index), '', ...audience.name.slice(index)],
            },
          ])
        )
        return newAudiences
      })
    } else {
      setAudiencesMap((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: [...prev[id].name.slice(0, index), '', ...prev[id].name.slice(index)],
        },
      }))
    }
  }

  const handleConfirm = () => {
    const newAudiences = Object.fromEntries(
      Object.entries(audiencesMap).map(([key, audience]) => [key, transformName.out(audience)])
    )
    onSubmit(newAudiences)
    onClose()
  }

  useEffect(() => {
    setAudiencesMap(
      audiences.reduce(
        (acc, audience) => ({
          ...acc,
          [audience._id]: transformName.in(audience),
        }),
        {}
      )
    )
  }, [audiences])

  useEffect(() => {
    if (isOpen && audiences.length === 0) {
      onClose()
      toast.error('No audience selected.')
    }
  }, [isOpen, audiences])

  return (
    <Dialog open={isOpen} className="modal-bulk-edit-audience">
      <header className="modal-bulk-edit-audience__header">
        <h2>{actionLabel}</h2>
        <div
          className="modal-bulk-edit-audience__apply-to-all"
          title="Changes made in one audience will overwrite the others"
        >
          <CheckBox checked={isApplyAll} onChange={() => setIsApplyAll((prev) => !prev)} />
          Apply changes to all audiences
        </div>
        <IconButton
          onClick={onClose}
          aria-label="Close modal"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#3d3d3d',
          }}
        />
      </header>
      <section className="modal-bulk-edit-audience__audiences-list">
        <For
          each="audience"
          of={Object.values(audiencesMap).sort((a, b) =>
            new Date(a.updatedAt).getTime() > new Date(b.updatedAt).getTime() ? -1 : 1
          )}
        >
          <div key={audience._id} className="modal-bulk-edit-audience__audiences-item">
            <h4 className="modal-bulk-edit-audience__name">{transformName.out(audience).name}</h4>
            <NameInput
              label="Audience Name"
              value={audience.name}
              onChange={handleNameInput(audience._id)}
              addInput={handleAddNameInput(audience._id)}
            />
            <Select
              label="Regions"
              className="audience-creator__regions-field"
              isMulti
              isSearchable
              useReactSelect
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              options={regions?.filter((region) => {
                return !audience?.regions?.some(
                  (audienceRegion) => audienceRegion.name === region.name
                )
              })}
              value={audience?.regions?.sort((a, b) => (a.name > b.name ? 1 : -1))}
              onChange={(newRegions) => {
                handleChange(audience._id, 'regions', newRegions || [])
              }}
            />
            <Select
              label="Destinations"
              isMulti
              isSearchable
              useReactSelect
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              options={destinations.filter((destination) => {
                return !audience.destinations?.some(
                  (audienceDestination) => audienceDestination.name === destination.name
                )
              })}
              value={audience.destinations
                ?.sort((a, b) => (a.name > b.name ? 1 : -1))
                ?.map((destination) => ({
                  ...destination,
                  label: destination.name,
                }))}
              onChange={(newDestinations) => {
                handleChange(audience._id, 'destinations', newDestinations || [])
              }}
            />
            <SelectedDestinations
              audience={convertObjAttrsSnakeToCamelCase(audience)}
              onInputChange={handleDestinationsConfigChange(audience)}
              schema={schema}
              disabled={false}
            />
          </div>
        </For>
      </section>
      <footer className="modal-bulk-edit-audience__footer">
        <div className="modal-bulk-edit-audience__actions">
          <Button onClick={onClose} theme={BUTTON_THEME.SECONDARY}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>{actionLabel}</Button>
        </div>
      </footer>
    </Dialog>
  )
}

export default ModalBulkEditAudience
